import type { ReactNode } from "react";

import { useCommitStatusColors } from "~/hooks/useCommitStatusColors";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import type { GitDetails } from "~/types";
import {
  cn,
  formatCommitAge,
  formatGitSha,
  truncateCommitMessage,
} from "~/utils";
import { formatRelativeUtc } from "~/utils/time";

import { GitButton } from "./GitButton";
import { Icon, SpinnerIcon } from "./icon";

interface GitInfoProps {
  gitDetails?: GitDetails;
  footer?: ReactNode;
  className?: string;
  showDetails?: boolean;
  isByoc?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function GitInfo({
  gitDetails,
  footer,
  className,
  showDetails = false,
  isByoc = false,
  onRefresh,
  isRefreshing = false,
}: GitInfoProps) {
  const currentSha = gitDetails?.current_commit?.sha;
  const currentCommitDate = gitDetails?.current_commit?.date;
  const latestCommitDetails = gitDetails?.latest_commit;
  const latestSha = latestCommitDetails?.sha;
  const behindBy = gitDetails?.behind_by;

  const colors = useCommitStatusColors(
    behindBy ?? undefined,
    isByoc,
    currentCommitDate,
  );
  const [copiedText, copy, resetCopiedText] = useCopyToClipboard();

  // Generate GitHub diff URL when we have both current and latest commits
  const diffUrl =
    currentSha && latestSha
      ? `https://github.com/turbopuffer/turbopuffer/compare/${currentSha}...${latestSha}`
      : null;

  const handleCopy = async (text: string) => {
    await copy(text);
    setTimeout(() => resetCopiedText(), 2000);
  };

  const latestUnknown =
    gitDetails?.current_commit != null && gitDetails?.latest_commit === null;

  if (!showDetails) {
    const summary = (
      <div className="flex items-center gap-2">
        {copiedText === currentSha ? (
          <Icon name="Check" size={16} className="text-green-600" />
        ) : (
          <Icon name="GitCommitHorizontal" size={16} className={colors.icon} />
        )}
        <span className={cn("font-mono text-xs", colors.text)}>
          {currentSha ? formatGitSha(currentSha) : "unknown"}
        </span>
      </div>
    );

    const summaryClassName = cn(
      "flex flex-col justify-between gap-3 rounded-sm border p-2 transition-colors",
      currentSha && "cursor-pointer hover:bg-blue-100 hover:text-blue-800",
      !currentSha && "cursor-default",
      colors.bg,
      colors.border,
    );

    const commitBox = currentSha ? (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCopy(currentSha);
        }}
        className={summaryClassName}
      >
        {summary}
      </button>
    ) : (
      <div className={summaryClassName}>{summary}</div>
    );

    const commitAge = currentCommitDate
      ? formatCommitAge(currentCommitDate)
      : null;

    return (
      <div className={cn("flex min-w-0 items-center gap-2", className)}>
        <div className="shrink-0">{commitBox}</div>
        {commitAge ? (
          <span
            className="min-w-0 truncate text-xs text-gray-500"
            title={commitAge}
          >
            ({commitAge})
          </span>
        ) : null}
        {latestUnknown ? (
          <span
            className="shrink-0 text-gray-400"
            title="Could not fetch latest git commit"
          >
            <Icon name="CircleAlert" size={16} />
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border border-gray-200 bg-gray-50 p-3 text-sm",
        className,
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row md:gap-16">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon
              name="GitCommitHorizontal"
              size={16}
              className={colors.icon}
            />
            <span className="font-mono text-sm font-medium">
              {currentSha ? formatGitSha(currentSha) : "unknown"}
            </span>
            {currentSha ? (
              <GitButton
                icon={copiedText === currentSha ? "Check" : "Copy"}
                size={16}
                className={cn(copiedText === currentSha && "text-green-600")}
                title="Copy full SHA"
                onClick={() => handleCopy(currentSha)}
              />
            ) : null}
            {currentSha && behindBy ? (
              <Status commitsBehind={behindBy} />
            ) : null}
            {latestUnknown ? (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Icon name="CircleAlert" size={16} />
                Could not fetch latest git commit
              </span>
            ) : null}
            {onRefresh && (!behindBy || behindBy === 0) ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                title="Check for newer commits"
              >
                {isRefreshing ? (
                  <SpinnerIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <Icon name="RefreshCw" size={16} />
                )}
              </button>
            ) : null}
          </div>
          {gitDetails?.current_commit?.date ? (
            <span className="text-xs text-gray-500">
              {formatRelativeUtc(
                new Date(gitDetails.current_commit.date),
                new Date(),
              )}
            </span>
          ) : null}
          {showDetails && gitDetails?.current_commit?.message ? (
            <span className="max-w-xs break-words text-xs text-gray-600 md:max-w-none">
              {truncateCommitMessage(gitDetails.current_commit.message)}
            </span>
          ) : null}
        </div>

        {behindBy != null && behindBy > 0 ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="font-mono text-sm font-medium">
                <span className="text-gray-700">latest passing: </span>
                <span className="text-green-700">
                  {latestSha ? formatGitSha(latestSha) : "unknown"}
                </span>
              </p>
              {latestSha ? (
                <GitButton
                  icon={copiedText === latestSha ? "Check" : "Copy"}
                  size={16}
                  className={cn(copiedText === latestSha && "text-green-600")}
                  title="Copy full SHA"
                  onClick={() => handleCopy(latestSha)}
                />
              ) : null}
              {diffUrl ? (
                <GitButton
                  icon="GitCompare"
                  title="View diff on GitHub"
                  href={diffUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ) : null}
              {onRefresh ? (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                  title="Refresh latest commit"
                >
                  {isRefreshing ? (
                    <SpinnerIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <Icon name="RefreshCw" size={16} />
                  )}
                </button>
              ) : null}
            </div>
            {latestCommitDetails ? (
              <>
                <span className="text-xs text-gray-500">
                  {formatRelativeUtc(
                    new Date(latestCommitDetails.date),
                    new Date(),
                  )}
                </span>
                <span className="max-w-xs break-words text-xs text-gray-600 md:max-w-none">
                  {truncateCommitMessage(latestCommitDetails.message)}
                </span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      {footer ? (
        <div className="-mx-3 mt-3 border-t border-gray-200 px-3 pt-3">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

const Status = ({ commitsBehind }: { commitsBehind: number }) => {
  return (
    <div className="flex items-center gap-2">
      {commitsBehind > 0 ? (
        <span className="text-xs font-medium text-orange-600">
          {`${commitsBehind} ${commitsBehind === 1 ? "commit" : "commits"} behind`}
        </span>
      ) : (
        <>
          <Icon name="CircleCheck" size={16} className="text-green-600" />
          <span className="text-xs font-medium text-green-700">latest</span>
        </>
      )}
    </div>
  );
};
